import ProductOptionsEditor, {
  type OptionRow,
  optionsFromApi,
  serializeOptions,
} from "components/product-options-editor";
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Switch } from "components/ui/switch";
import { Textarea } from "components/ui/textarea";
import PageHeader from "components/page-header";
import ReadyTimeField from "components/ready-time-field";
import { Icon } from "components/ui/icon";
import { cn } from "lib/utils";
import { toast } from "sonner";
import { productUpdateAction } from "action/product-action";
import { categories as defaultCategories } from "helpers/constants";
import { useSessionControl } from "hooks/use-session-control";
import { getCategoriesAction, createCategoryAction } from "action/category-action";
import { Plus } from "lucide-react";

export interface EditProductProps {
  id: string;
  name: string;
  category: string;
  price: number;
  description: string;
  available_after_days?: number;
  featured: boolean;
  today_special: boolean;
  image_url: string;
  options?: { id: string; name: string; price: string | number }[];
  onCancel?: () => void;
  onUpdateDone?: () => void;
}

const schema = z.object({
  name: z.string().min(1, { message: "Your product name is required." }),
  category: z.string().min(1, { message: "Please select a category" }),
  price: z.number().min(1, { message: "Please enter a price" }),
  description: z.string().min(7, { message: "Please enter a description" }),
  available_after_days: z.number().min(0, { message: "Availability cannot be less than 0" }).optional(),
  featured: z.boolean(),
  today_special: z.boolean(),
  thumbnailImage: z
    .instanceof(File, { message: "Please add a Product Image" })
    .optional()
    .or(z.string().url({ message: "Invalid image URL." })),
});

const EditProductComponent: React.FC<EditProductProps> = (props) => {
  const [searchCategoryTerm, setSearchCategoryTerm] = useState("");
  const [allCategories, setAllCategories] = useState<string[]>(defaultCategories);
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    const response = await getCategoriesAction();
    if (response.status === 200 && response.body) {
      const customCategoryNames = response.body.map(cat => cat.name);
      // Merge and remove duplicates
      const merged = Array.from(new Set([...defaultCategories, ...customCategoryNames]));
      setAllCategories(merged);
    }
  };

  const handleAddCustomCategory = async () => {
    const name = searchCategoryTerm.trim();
    if (!name) return;

    // Normalization: Title Case
    const normalizedName = name.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');

    if (normalizedName.length < 2) {
      toast.error("Category name is too short");
      return;
    }

    if (allCategories.includes(normalizedName)) {
      setValue("category", normalizedName);
      setSearchCategoryTerm("");
      return;
    }

    setIsAddingCategory(true);
    const response = await createCategoryAction(normalizedName);
    setIsAddingCategory(false);

    if (response.status === 201) {
      toast.success("Category added successfully");
      setAllCategories(prev => [...prev, response.body.name]);
      setValue("category", response.body.name);
      setSearchCategoryTerm("");
    } else {
      toast.error(response.error || "Failed to add category");
    }
  };

  // Filter categories based on the search term
  const filteredCategories = allCategories.filter((category) =>
    category.toLowerCase().includes(searchCategoryTerm.toLowerCase())
  );

  const {
    register,
    setValue,
    watch,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    mode: "all",
    defaultValues: {
      name: props.name || "",
      category: props.category || "",
      price: props.price || 0,
      description: props.description || "",
      available_after_days: props.available_after_days || 0,
      featured: props.featured || false,
      today_special: props.today_special || false,
      thumbnailImage: props.image_url || null,
    },
  });
  const [isFeatured, setIsFeatured] = React.useState(props.featured);
  const [isTodaySpecial, setIsTodaySpecial] = React.useState(
    props.today_special
  );
  const { sessionControl } = useSessionControl();

  const [options, setOptions] = React.useState<OptionRow[]>(() =>
    optionsFromApi(props.options),
  );

  const onSubmit = (data: z.infer<typeof schema>) => {
    const serialized = serializeOptions(options);
    if (serialized.error) {
      toast.error(serialized.error);
      return;
    }
    const toastId = toast.loading("Updating product...");

    const formData = new FormData();
    formData.append("id", props.id);
    formData.append("name", data.name);
    formData.append("category", data.category);
    formData.append("price", data.price.toString());
    if (
      typeof data.available_after_days !== "undefined" &&
      data.available_after_days !== null
    ) {
      formData.append(
        "available_after_days",
        String(data.available_after_days)
      );
    }
    formData.append("description", data.description);
    formData.append("featured", data.featured.toString());
    formData.append("todayDeal", data.today_special.toString());
    if (data.thumbnailImage instanceof File) {
      formData.append("thumbnailImage", data.thumbnailImage);
    }
    // Always sent, so removing every option clears them.
    formData.append("options", serialized.json);

    // console.log("Form data to submit:", data);
    productUpdateAction(props.id, formData)
      .then((response) => {
        if (response.status === 200) {
          toast.dismiss(toastId);
          toast.success("Product updated successfully");
          reset();
          props.onUpdateDone && props.onUpdateDone();
        } else {
          sessionControl(response.status);
          toast.dismiss(toastId);
          toast.error(response.error);
        }
      })
      .catch((err: any) => {
        toast.dismiss(toastId);
        toast.error("Failed to create product, An error occurred");
        console.error(err);
      });
  };

  return (
    <div className="w-full">
      <PageHeader
        eyebrow="Listed Products"
        title={<>Edit Product: {props.name}</>}
        actions={
          <Button variant="outline" onClick={() => props.onCancel && props.onCancel()}>
            Close
          </Button>
        }
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-12 gap-5">
          <div className="col-span-12 space-y-5 lg:col-span-8">
            <Card>
              <CardHeader className="border-b px-6 py-5 mb-6">
                <CardTitle className="text-lg">Product Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Name */}
                <div className="flex flex-col gap-2">
                  <Label className="" htmlFor="name">
                    Product Name
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Name"
                    {...register("name")}
                    className={cn("", {
                      "border-destructive": errors.name,
                    })}
                  />
                </div>
                {errors.name && (
                  <p className="text-destructive mt-2 text-sm">
                    {errors.name.message}
                  </p>
                )}

                {/* Category */}
                <div className="flex flex-col gap-2">
                  <Label>Category</Label>
                  <Select onValueChange={(e) => setValue("category", e)}>
                    <SelectTrigger>
                      <SelectValue placeholder={props.category} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>Category</SelectLabel>
                        {/* Search Input */}
                        <div className="p-2">
                          <Input
                            ref={searchInputRef}
                            type="text"
                            placeholder="Search Categories"
                            value={searchCategoryTerm}
                            onChange={(e) =>
                              setSearchCategoryTerm(e.target.value)
                            }
                            onPointerDown={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                              // Prevent Radix from handling keyboard events (like type-ahead) while typing
                              e.stopPropagation();

                              if (e.key === "Enter" && filteredCategories.length > 0) {
                                e.preventDefault();
                                const matched =
                                  filteredCategories.find(
                                    (c) =>
                                      c.toLowerCase() ===
                                      searchCategoryTerm.toLowerCase()
                                  ) || filteredCategories[0];
                                setValue("category", matched);
                                setSearchCategoryTerm("");
                                // Force close select by blurring the input
                                searchInputRef.current?.blur();
                              }
                            }}
                            className="w-full p-1 px-2 border rounded text-sm"
                          />
                        </div>
                        {/* Render filtered categories */}
                        {filteredCategories.length > 0 ? (
                          filteredCategories.map((category) => (
                            <SelectItem key={category} value={category}>
                              {category}
                            </SelectItem>
                          ))
                        ) : (
                          <div className="p-2 space-y-2">
                            <div className="text-default-500 text-sm flex justify-center">
                              Can't find what you are looking for?
                            </div>
                            {searchCategoryTerm.trim().length >= 2 && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="w-full flex items-center justify-center gap-2"
                                onClick={handleAddCustomCategory}
                                disabled={isAddingCategory}
                              >
                                <Plus className="w-4 h-4" />
                                Add "{searchCategoryTerm}"
                              </Button>
                            )}
                          </div>
                        )}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
                {errors.category && (
                  <p className="text-destructive mt-2 text-sm">
                    {errors.category.message}
                  </p>
                )}

                {/* Price */}
                <div className="flex flex-col gap-2">
                  <Label className="" htmlFor="price">
                    Price(₦)
                  </Label>
                  <Input
                    id="price"
                    type="number"
                    placeholder="Price"
                    {...register("price", { valueAsNumber: true })}
                    className={cn("", {
                      "border-destructive": errors.price,
                    })}
                  />
                </div>
                {errors.price && (
                  <p className="text-destructive mt-2 text-sm">
                    {errors.price.message}
                  </p>
                )}

                {/* Minimum purchase qty */}
                {/* <div className="flex flex-col gap-2">
                                    <Label className="" htmlFor="qty">
                                        Minimum Purchase Qty
                                    </Label>
                                </div> */}

                {/* Available after days moved to Availability section below */}

                {/* <Input id="qty" type="text" placeholder="1" disabled={true}/>
                                </div>

                                {/* Product tags */}
                <div className="flex flex-col gap-2">
                  <Label className="" htmlFor="tags">
                    Tags
                  </Label>
                  <Input
                    disabled={true}
                    id="tags"
                    type="text"
                    placeholder="Type & hit enter to add a tag"
                  />
                </div>

                {/* Barcode */}
                <div className="flex flex-col gap-2">
                  <Label className="" htmlFor="brcode">
                    Barcode
                  </Label>
                  <Input
                    disabled={true}
                    id="brcode"
                    type="text"
                    placeholder="Code"
                  />
                </div>

                {/* Is refundable switch */}
                <div className="flex items-center justify-between gap-4">
                  <Label>Refundable</Label>
                  <div className="flex-none">
                    <Switch disabled={true} color="success" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Product description */}
            <Card>
              <CardHeader className="border-b px-6 py-5 mb-6">
                <CardTitle className="text-lg">Options</CardTitle>
              </CardHeader>
              <CardContent>
                <ProductOptionsEditor value={options} onChange={setOptions} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="border-b px-6 py-5 mb-6">
                <CardTitle className="text-lg">Product Description</CardTitle>
              </CardHeader>
              <CardContent className="flex">
                <Label>Description</Label>
                <div className="flex-1">
                  <Textarea
                    id="description"
                    placeholder="Product description..."
                    {...register("description")}
                    className={cn("", {
                      "border-destructive": errors.description,
                    })}
                  />
                  {errors.description && (
                    <p className="text-destructive mt-2 text-sm">
                      {errors.description.message}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Product images */}
            <Card>
              <CardHeader className="border-b px-6 py-5 mb-6">
                <CardTitle className="text-lg">Product Images</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Gallery images */}
                {/*<div className="flex items-center space-x-6 rtl:space-x-reverse">*/}
                {/*    <Label className="inline-flex text-sm font-normal ">*/}
                {/*        Gallery Images (600 X 600)*/}
                {/*    </Label>*/}
                {/*    <div className="flex-1">*/}
                {/*        <Input*/}
                {/*            disabled={true}*/}
                {/*            className="read-only:leading-0 py-1.5 h-full ps-0"*/}
                {/*            type="file"*/}
                {/*        />*/}
                {/*    </div>*/}
                {/*</div>*/}

                {/* Thumb images */}
                <div className="flex items-center space-x-6 rtl:space-x-reverse">
                  <Label className="inline-flex text-sm font-normal ">
                    Thumb Images (600 X 600)
                  </Label>
                  <div className="flex-1">
                    <Input
                      className={cn("read-only:leading-0 py-1.5 h-full ps-0", {
                        "border-destructive": errors.thumbnailImage,
                      })}
                      type="file"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setValue("thumbnailImage", file);
                        }
                      }}
                    />
                    {errors.thumbnailImage && (
                      <p className="text-destructive mt-2 text-sm">
                        {errors.thumbnailImage.message}
                      </p>
                    )}
                    {typeof props.image_url === "string" && (
                      <a
                        href={props.image_url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <div className={"flex mt-2 items-center"}>
                          Current Image
                          <Icon
                            icon="heroicons:link"
                            className="h-5 w-5 ml-1"
                          />
                        </div>
                      </a>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Availability */}
            <Card>
              <CardHeader className="border-b px-6 py-5 mb-6">
                <CardTitle className="text-lg">When is it ready?</CardTitle>
              </CardHeader>
              <CardContent>
                <ReadyTimeField
                  value={watch("available_after_days") ?? 0}
                  onChange={(days) =>
                    setValue("available_after_days", days, { shouldValidate: true })
                  }
                />
              </CardContent>
            </Card>

            {/* SEO meta tags */}
            {/* <Card>
              <CardHeader className="border-b px-6 py-5 mb-6">
                <CardTitle className="text-lg">SEO Meta Tags</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex">
                  <Label className="w-[130px]">Meta Title</Label>
                  <Input
                    disabled={true}
                    id="meta_title"
                    type="text"
                    placeholder="Title"
                  />
                </div>

                <div className="flex">
                  <Label className="w-[130px]">Description</Label>
                  <Textarea
                    disabled={true}
                    id="meta_description"
                    placeholder="Write something for your product SEO"
                  />
                </div>
                <div className="flex items-center ">
                  <Label className="flex-none w-[130px] ">Meta Image</Label>
                  <Input
                    disabled={true}
                    className="read-only:leading-0 py-1.5 h-full ps-0"
                    type="file"
                  />
                </div>
              </CardContent>
            </Card> */}
          </div>
          <div className="col-span-12 space-y-5 lg:col-span-4 lg:sticky lg:top-24 lg:self-start">
            {/* Low stock warning quantity */}
            {/* <Card>
              <CardHeader className="border-b px-6 py-5 mb-6">
                <CardTitle className="text-lg">Low Stock Quantity Warning</CardTitle>
              </CardHeader>
              <CardContent className="flex items-center">
                <Label>Quantity</Label>
                <Input
                  disabled={true}
                  id="qty_warning"
                  type="text"
                  placeholder="1"
                />
              </CardContent>
            </Card> */}

            {/* Featured */}
            <Card>
              <CardHeader className="border-b px-6 py-5 mb-6">
                <CardTitle className="text-lg">Featured</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between">
                  <Label className="text-sm font-normal">Status</Label>
                  <div>
                    <Switch
                      checked={isFeatured}
                      onCheckedChange={(checked) => {
                        setValue("featured", checked);
                        setIsFeatured(checked);
                      }}
                      color="success"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Today's Special */}
            <Card>
              <CardHeader className="border-b px-6 py-5 mb-6">
                <CardTitle className="text-lg">Today's Special</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between">
                  <Label className="text-sm font-normal">Status</Label>
                  <div>
                    <Switch
                      checked={isTodaySpecial}
                      onCheckedChange={(checked) => {
                        setValue("today_special", checked);
                        setIsTodaySpecial(checked);
                      }}
                      color="success"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Flash Deal */}
            {/* <Card>
              <CardHeader className="border-b px-6 py-5 mb-6">
                <CardTitle className="text-lg">Flash Deal</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center">
                  <Label>Discount</Label>
                  <Input
                    id="discount"
                    type="text"
                    placeholder="0"
                    disabled={true}
                  />
                </div>

                <div className="flex items-center">
                  <Label>Type</Label>
                  <Select disabled={true}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>Type</SelectLabel>
                        <SelectItem value="percent">Percent</SelectItem>
                        <SelectItem value="amount">Amount</SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card> */}
          </div>
          <div className="col-span-12 flex justify-end">
            <Button type="submit" onClick={handleSubmit(onSubmit)}>
              Update Product
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditProductComponent;
